import { useState } from "react";
import { ArrowUpRight, Lock, ArrowRight } from "lucide-react";
import { SQUARE_BOOKING_URL, api } from "@/lib/api";

const BackButton = ({ onClick, children = "Back" }) => (
  <button type="button" onClick={onClick} className="text-[12px] uppercase tracking-[0.22em] text-[#55524C]">
    {children}
  </button>
);

const Field = ({ id, label, testId, value, onChange, type = "text", required = true, ...rest }) => (
  <div>
    <label className="label-eyebrow" htmlFor={id}>{label}</label>
    <input id={id} type={type} required={required} data-testid={testId} className="input-soft" value={value} onChange={onChange} {...rest} />
  </div>
);

const ChooseStage = ({ onNewClient }) => (
  <div className="max-w-[1100px] mx-auto px-6 sm:px-10 mt-24 grid grid-cols-1 md:grid-cols-2 gap-8">
    <a
      href={SQUARE_BOOKING_URL}
      target="_blank"
      rel="noreferrer"
      data-testid="book-existing-cta"
      className="card-edge p-10 sm:p-12 bg-[#F0EDE6] text-left group hover:bg-[#EAE7E0] transition-colors block"
    >
      <div className="label-eyebrow">Returning</div>
      <h2 className="serif mt-5 text-[30px] sm:text-[38px] leading-tight">Existing client</h2>
      <p className="mt-5 text-[15px] leading-[1.8] text-[#55524C]">
        Continue straight to online booking.
      </p>
      <span className="mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-medium group-hover:text-[#A96A53] transition-colors">
        Book your next appointment <ArrowUpRight size={14} strokeWidth={1.6} />
      </span>
    </a>

    <button
      data-testid="book-new-cta"
      onClick={onNewClient}
      className="card-edge p-10 sm:p-12 bg-[#F0EDE6] text-left group hover:bg-[#EAE7E0] transition-colors"
    >
      <div className="label-eyebrow">First visit</div>
      <h2 className="serif mt-5 text-[30px] sm:text-[38px] leading-tight">New client</h2>
      <p className="mt-5 text-[15px] leading-[1.8] text-[#55524C]">
        A short introduction so I can prepare the room properly.
      </p>
      <span className="mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-medium group-hover:text-[#A96A53] transition-colors">
        First visit <ArrowRight size={14} strokeWidth={1.6} />
      </span>
    </button>
  </div>
);

const GenderStage = ({ onWoman, onMan, onBack }) => (
  <div className="max-w-[820px] mx-auto px-6 sm:px-10 mt-20" data-testid="gender-step">
    <div className="card-edge p-10 sm:p-14 bg-[#F7F6F2]">
      <div className="label-eyebrow">A gentle question</div>
      <h2 className="serif mt-5 text-[30px] sm:text-[40px] leading-tight">Which best describes you?</h2>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button data-testid="gender-woman" onClick={onWoman}
          className="border border-[#D5CDBF] py-8 hover:bg-[#EAE7E0] transition-colors serif text-[22px]">
          Woman
        </button>
        <button data-testid="gender-man" onClick={onMan}
          className="border border-[#D5CDBF] py-8 hover:bg-[#EAE7E0] transition-colors serif text-[22px]">
          Man
        </button>
      </div>
      <BackButton onClick={onBack}>← Back</BackButton>
    </div>
  </div>
);

const WomanForm = ({ form, update, onSubmit, onBack, status, errorMsg }) => (
  <div className="max-w-[900px] mx-auto px-6 sm:px-10 mt-16">
    <form onSubmit={onSubmit} data-testid="booking-intake-form" className="border border-[#D5CDBF] p-8 sm:p-12 bg-[#F7F6F2] space-y-6">
      <div className="flex items-center gap-3 text-[#55524C]">
        <Lock size={14} strokeWidth={1.4} />
        <span className="label-eyebrow">A gentle introduction</span>
      </div>
      <h2 className="serif text-[28px] sm:text-[36px] leading-tight">Before your first visit</h2>
      <p className="text-[14px] leading-relaxed text-[#55524C] max-w-xl">
        Your full name (no initials), an email, and a number where I can reach you. The studio address is shared on confirmation.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Field id="fn" label="First name" testId="intake-first-name" value={form.first_name} onChange={update("first_name")} minLength={2} pattern="[A-Za-zÀ-ÖØ-öø-ÿ\-' ]{2,}" />
        <Field id="ln" label="Surname" testId="intake-last-name" value={form.last_name} onChange={update("last_name")} minLength={2} pattern="[A-Za-zÀ-ÖØ-öø-ÿ\-' ]{2,}" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Field id="em" label="Email" testId="intake-email" type="email" value={form.email} onChange={update("email")} />
        <Field id="ph" label="Phone" testId="intake-phone" value={form.phone} onChange={update("phone")} minLength={6} />
      </div>
      <div><label className="label-eyebrow" htmlFor="no">A note (optional)</label>
        <textarea id="no" rows={3} data-testid="intake-note" className="input-soft resize-none" value={form.note} onChange={update("note")} /></div>
      {errorMsg && <p data-testid="intake-error" className="text-[13px]" style={{ color: "var(--terracotta)" }}>{errorMsg}</p>}
      <div className="flex items-center gap-4 pt-2">
        <button type="submit" data-testid="intake-submit" disabled={status === "loading"} className="btn-primary">
          {status === "loading" ? "Sending…" : "Continue"}
        </button>
        <BackButton onClick={onBack} />
      </div>
    </form>
  </div>
);

const WomanSuccess = ({ firstName }) => (
  <div className="max-w-[900px] mx-auto px-6 sm:px-10 mt-16" data-testid="intake-success">
    <div className="border border-[#D5CDBF] p-8 sm:p-12 bg-[#F0EDE6]">
      <div className="label-eyebrow">Thank you, {firstName}</div>
      <h2 className="serif mt-4 text-[28px] sm:text-[40px] leading-tight">You're warmly welcome.</h2>
      <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-[#2E2B27]">
        You can choose a time below — the studio address will follow with your confirmation.
      </p>
      <a href={SQUARE_BOOKING_URL} target="_blank" rel="noreferrer" data-testid="intake-square-cta" className="btn-primary mt-10 inline-flex">
        Begin Your Ritual <ArrowUpRight size={14} strokeWidth={1.6} />
      </a>
    </div>
  </div>
);

const ManStage = ({ form, update, referrer, setReferrer, showForm, setShowForm, onSubmit, onBack, status, errorMsg }) => (
  <div className="max-w-[820px] mx-auto px-6 sm:px-10 mt-16" data-testid="man-flow">
    <div className="border border-[#D5CDBF] p-8 sm:p-12 bg-[#F7F6F2] space-y-8">
      <div>
        <div className="label-eyebrow">A note first</div>
        <p className="mt-6 text-[16px] leading-[1.85] text-[#2E2B27]">
          Studio A is a private home-based wellness studio.
        </p>
        <p className="mt-4 text-[16px] leading-[1.85] text-[#2E2B27]">
          Male clients are considered for facial treatments only, and are accepted by referral or enquiry.
        </p>
        <p className="mt-4 text-[16px] leading-[1.85] text-[#2E2B27]">
          Body massage treatments are not available for male clients.
        </p>
      </div>

      {!showForm && (
        <div className="border-t border-[#D5CDBF] pt-8">
          <label htmlFor="ref" className="label-eyebrow">Who referred you?</label>
          <input id="ref" data-testid="man-referrer" className="input-soft" value={referrer} onChange={(e) => setReferrer(e.target.value)} placeholder="Full name (optional)" />
          <div className="flex items-center gap-4 pt-8">
            <button type="button" onClick={() => setShowForm(true)} data-testid="man-continue" className="btn-primary">
              Continue to enquiry
            </button>
            <BackButton onClick={onBack} />
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={onSubmit} className="space-y-6 border-t border-[#D5CDBF] pt-8" data-testid="man-enquiry-form">
          <p className="text-[14px] text-[#55524C] leading-relaxed">
            Please share the following. Anissa reviews each enquiry personally. Online booking is not available for new male clients.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field id="mf" label="First name" testId="man-first" value={form.first_name} onChange={update("first_name")} />
            <Field id="ml" label="Surname" testId="man-last" value={form.last_name} onChange={update("last_name")} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field id="me" label="Email" testId="man-email" type="email" value={form.email} onChange={update("email")} />
            <Field id="mp" label="Phone" testId="man-phone" value={form.phone} onChange={update("phone")} />
          </div>
          <div><label className="label-eyebrow" htmlFor="mn">Reason for booking</label>
            <textarea id="mn" rows={3} required data-testid="man-reason" className="input-soft resize-none" value={form.note} onChange={update("note")} /></div>
          {errorMsg && <p className="text-[13px]" style={{ color: "var(--terracotta)" }}>{errorMsg}</p>}
          <div className="flex items-center gap-4 pt-2">
            <button type="submit" data-testid="man-submit" disabled={status === "loading"} className="btn-primary">
              {status === "loading" ? "Sending…" : "Send enquiry"}
            </button>
            <BackButton onClick={() => setShowForm(false)} />
          </div>
        </form>
      )}
    </div>
  </div>
);

const ManSuccess = () => (
  <div className="max-w-[820px] mx-auto px-6 sm:px-10 mt-16" data-testid="man-success">
    <div className="border border-[#D5CDBF] p-8 sm:p-12 bg-[#F0EDE6]">
      <div className="label-eyebrow">Enquiry received</div>
      <h2 className="serif mt-4 text-[28px] sm:text-[36px] leading-tight">Thank you.</h2>
      <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-[#2E2B27]">
        Anissa will review your enquiry personally and reply within a few days.
      </p>
    </div>
  </div>
);

const REASSURANCE = [
  ["When you arrive", "A quiet welcome. No waiting room — straight into the studio."],
  ["Before the treatment", "A short conversation about your skin, your week, what's been a lot lately."],
  ["During and after", "The treatment is shaped from there. You can talk, or rest. Never rushed."],
];

const ReassuranceRow = () => (
  <div className="max-w-[1100px] mx-auto px-6 sm:px-10 mt-24 grid grid-cols-1 md:grid-cols-3 gap-10">
    {REASSURANCE.map(([title, copy]) => (
      <div key={title} className="border-t border-[#D5CDBF] pt-8">
        <div className="label-eyebrow">{title}</div>
        <p className="mt-4 text-[16px] leading-[1.8] text-[#55524C]">{copy}</p>
      </div>
    ))}
  </div>
);

// stages: choose → existing OR new-gender → new-woman OR new-man → done
export default function Book() {
  const [stage, setStage] = useState("choose");
  const [referrer, setReferrer] = useState("");
  const [showManForm, setShowManForm] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", note: "" });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitWoman = async (e) => {
    e.preventDefault();
    setErrorMsg(""); setStatus("loading");
    try {
      await api.post("/booking-intake", form);
      setStatus("done");
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "Please check the details and try again.");
      setStatus("error");
    }
  };

  const submitMan = async (e) => {
    e.preventDefault();
    setErrorMsg(""); setStatus("loading");
    try {
      await api.post("/contact", {
        name: `${form.first_name} ${form.last_name}`.trim(),
        email: form.email,
        phone: form.phone,
        message: `[Male client enquiry] Reason: ${form.note}\nReferral: ${referrer || "none"}`,
      });
      setStatus("done");
    } catch (err) {
      console.error("Man enquiry submit failed:", err);
      setErrorMsg("Please try again.");
      setStatus("error");
    }
  };

  return (
    <div data-testid="book-page" className="pt-28 lg:pt-44 pb-32">
      <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
        <div className="label-eyebrow">Book</div>
        <h1 className="serif mt-7 text-[48px] sm:text-[80px] leading-[1] tracking-tight">
          Choose your <span className="italic">time</span>.
        </h1>
        <p className="mt-12 max-w-xl text-[18px] leading-[1.8] text-[#2E2B27]">
          Studio A is a private home-based studio in Bondi Beach, by appointment only. There's no pressure here, no judgement, and no rush.
        </p>
      </div>

      {stage === "choose" && <ChooseStage onNewClient={() => setStage("gender")} />}

      {stage === "gender" && (
        <GenderStage
          onWoman={() => setStage("woman")}
          onMan={() => setStage("man")}
          onBack={() => setStage("choose")}
        />
      )}

      {stage === "woman" && status !== "done" && (
        <WomanForm form={form} update={update} onSubmit={submitWoman} onBack={() => setStage("gender")} status={status} errorMsg={errorMsg} />
      )}
      {stage === "woman" && status === "done" && <WomanSuccess firstName={form.first_name} />}

      {stage === "man" && status !== "done" && (
        <ManStage
          form={form}
          update={update}
          referrer={referrer}
          setReferrer={setReferrer}
          showForm={showManForm}
          setShowForm={setShowManForm}
          onSubmit={submitMan}
          onBack={() => setStage("gender")}
          status={status}
          errorMsg={errorMsg}
        />
      )}
      {stage === "man" && status === "done" && <ManSuccess />}

      <ReassuranceRow />
    </div>
  );
}
